/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import { useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import Wizard from "@/components/Wizard";
import { Button } from "@/components/ui/button";
import { HOME } from "@/constants/testIds";
import { ArrowRight, Timer, Workflow, PhoneCall, Radar } from "lucide-react";

export default function Funnel() {
    const navigate = useNavigate();

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteHeader />

            {/* HERO */}
            <main role="main">
                <section
                    aria-labelledby="hero-heading"
                    className="relative overflow-hidden border-b border-border"
                >
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 -z-10 opacity-50 bg-grain"
                    />
                    <div
                        aria-hidden="true"
                        className="absolute -top-32 right-[-20%] -z-10 h-[420px] w-[420px] rounded-full bg-primary/15 blur-3xl"
                    />

                    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-12 md:gap-8 md:px-10 md:py-28">
                        <div className="md:col-span-7">
                            <p
                                data-testid={HOME.heroOverline}
                                className="font-mono text-xs uppercase tracking-[0.28em] text-primary"
                            >
                                ▪ Lead Dispatch Code Freeze · Q1 2026
                            </p>
                            <h1
                                id="hero-heading"
                                data-testid={HOME.heroHeadline}
                                className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
                            >
                                Your website is losing{" "}
                                <span className="text-primary">80% of its inbound leads</span>{" "}
                                on mobile.
                            </h1>
                            <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
                                When a homeowner requests a quote, if you don&apos;t call them back within{" "}
                                <span className="text-foreground">5 minutes</span>, your competitor gets paid.
                                SpeedToLead turns incoming web forms into instant automated phone calls
                                straight to your cell phone within{" "}
                                <span className="text-foreground">30 seconds</span>.
                            </p>

                            <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                                <Button
                                    data-testid={HOME.heroCta}
                                    onClick={() => scrollTo("diagnose")}
                                    className="rounded-none bg-primary px-8 py-7 text-sm font-semibold uppercase tracking-[0.2em] text-primary-foreground hover:bg-primary/90"
                                >
                                    Run Free 5-Minute Speed Test
                                    <ArrowRight className="ml-3 h-4 w-4" aria-hidden="true" />
                                </Button>
                                <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                    <span className="inline-block h-2 w-2 rounded-full bg-primary animate-pulseRed" aria-hidden="true" />
                                    Live latency probe · No fabricated metrics
                                </div>
                            </div>

                            <div className="mt-12 hairline w-32" aria-hidden="true" />

                            <dl className="mt-8 grid max-w-2xl grid-cols-3 gap-6 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                                <div>
                                    <dt>Avg. response</dt>
                                    <dd className="mt-1 font-display text-2xl font-bold text-foreground tabular-nums">
                                        30s
                                    </dd>
                                </div>
                                <div>
                                    <dt>Activation</dt>
                                    <dd className="mt-1 font-display text-2xl font-bold text-foreground tabular-nums">
                                        $149
                                    </dd>
                                </div>
                                <div>
                                    <dt>Niches served</dt>
                                    <dd className="mt-1 font-display text-2xl font-bold text-foreground">
                                        12+
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <aside
                            className="md:col-span-5"
                            aria-label="Visual diagnostic mock"
                        >
                            <div className="relative scanline border border-border bg-zinc-950 p-6">
                                <div className="flex items-center justify-between border-b border-border pb-3">
                                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
                                        ▪ Speed-Probe Console
                                    </p>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        v1.0.0
                                    </span>
                                </div>
                                <div className="mt-4 space-y-3 font-mono text-xs">
                                    <ConsoleLine label="GET" value="https://www.fixmysitekit.com" />
                                    <ConsoleLine label="DNS" value="38ms" />
                                    <ConsoleLine label="TLS" value="142ms" />
                                    <ConsoleLine label="TTFB" value="318ms" warn />
                                    <ConsoleLine label="FORM-ROUTE" value="email → SMTP queue" warn />
                                    <ConsoleLine label="HUMAN-RTT" value="00:46:12" danger />
                                    <ConsoleLine label="SpeedToLead" value="< 30s" ok />
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>

                {/* HOW SECTION */}
                <section
                    id="how"
                    aria-labelledby="how-heading"
                    className="border-b border-border bg-card/30"
                >
                    <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 md:grid-cols-12 md:px-10">
                        <div className="md:col-span-4">
                            <p className="font-mono text-xs uppercase tracking-[0.28em] text-primary">
                                ▪ How the routing works
                            </p>
                            <h2
                                id="how-heading"
                                className="mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl"
                            >
                                Form in. Phone out. No middleman.
                            </h2>
                            <p className="mt-4 text-sm text-muted-foreground">
                                A dedicated webhook URL replaces your email-based form handler.
                                The instant a payload arrives, our gateway initiates a voice
                                relay to your business line.
                            </p>
                        </div>
                        <div className="grid gap-4 md:col-span-8 md:grid-cols-3">
                            <FeatureBlock
                                icon={Workflow}
                                title="Drop-in webhook"
                                body="A single endpoint URL replaces your form's email destination. Works with WordPress, Webflow, Shopify."
                            />
                            <FeatureBlock
                                icon={Radar}
                                title="Real diagnostics"
                                body="We probe your site live and store the actual round-trip latency. No fake scans, ever."
                            />
                            <FeatureBlock
                                icon={PhoneCall}
                                title="Instant voice relay"
                                body="Mocked Twilio-compatible relay rings your cell within 30 seconds. Swap to your own Twilio creds anytime."
                            />
                        </div>
                    </div>
                </section>

                {/* WIZARD */}
                <section className="bg-background">
                    <div className="mx-auto max-w-5xl px-6 py-16 md:px-10">
                        <Wizard
                            onPaid={(token) =>
                                navigate(`/secure-ops-hub-99x/${token}`)
                            }
                        />
                    </div>
                </section>

                {/* NICHES */}
                <section
                    aria-labelledby="niches-heading"
                    className="border-y border-border bg-card/30"
                >
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <p className="font-mono text-xs uppercase tracking-[0.28em] text-primary">
                            ▪ Niche coverage
                        </p>
                        <h2 id="niches-heading" className="mt-3 font-display text-3xl font-bold">
                            Built for high-intent home services
                        </h2>
                        <ul className="mt-6 flex flex-wrap gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            {[
                                "Plumbing",
                                "Towing",
                                "Roofing",
                                "Junk Removal",
                                "Pest Control",
                                "HVAC",
                                "Locksmith",
                                "Lawn Care",
                                "Mobile Mechanic",
                                "Electrical",
                                "Window Cleaning",
                                "Auto Detail",
                            ].map((n) => (
                                <li
                                    key={n}
                                    className="border border-border bg-background px-3 py-2 text-foreground/80"
                                >
                                    {n}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </main>

            {/* Exclusive Territory License strip — discreet in-house program pointer */}
            <section
                className="border-t border-border bg-background/60"
                data-testid="franchise-strip"
            >
                <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-red-400/80 font-mono">
                            Exclusive Territory License
                        </div>
                        <div className="mt-1 text-sm text-foreground">
                            One operator per metro · $1,500 setup + $499/mo · by application only.
                        </div>
                    </div>
                    <a
                        href="/license"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground bg-foreground/5 hover:bg-foreground/10 border border-border rounded-lg px-4 py-2 transition"
                        data-testid="franchise-strip-cta"
                    >
                        Apply for territory →
                    </a>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}

function ConsoleLine({ label, value, ok, warn, danger }) {
    const tone = danger
        ? "text-primary"
        : warn
        ? "text-amber-300"
        : ok
        ? "text-emerald-300"
        : "text-muted-foreground";
    return (
        <div className="flex items-center justify-between border-b border-dashed border-border/70 pb-1 last:border-none">
            <span className="text-muted-foreground">{label}</span>
            <span className={tone}>{value}</span>
        </div>
    );
}

function FeatureBlock({ icon: Icon, title, body }) {
    return (
        <article className="border border-border bg-zinc-950/50 p-5">
            <div className="mb-4 grid h-9 w-9 place-items-center bg-primary/10 text-primary">
                <Icon className="h-4 w-4" aria-hidden="true" />
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        </article>
    );
}
